import { useEffect, useRef, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ValidateProjectName } from '@/connection/projects/validate-project-name';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  removeCreateProjectErrorFields,
  saveWorkingProjectByFields,
  setCreateProjectErrorFields,
} from '@/store/slices/project';
import {
  getIsLoadingWorkingProject,
  getWorkingProjectEventId,
  getWorkingProjectUuid,
  selectCreateProjectErrors,
  selectProjectChannel,
  selectProjectGlobalTemplateType,
  selectProjectIsAutoGeneratedName,
  selectProjectName,
  selectProjectSettings,
  selectWorkingProjectDefaultLanguage,
} from '@/store/slices/project/selectors';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { getDisplayProjectName, getStrippedProjectAutoNamePart } from '@/utils/get-project-name';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import toastTrigger from '@ui/toast/toast-trigger';
import Text from '@ui/text/text/text';
import OptionalComponent from '@ui/optional-component/optional-component';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import { updateTranslateKey } from '@/utils/update-translation-key';
import useHasPermission from '@/utils/authentication/use-has-permission';
import { EPermissions } from '@/types/permissions.types';
import { TPartialSessionWithUuid } from '@/types/session.types';
import useGetFontFamilyClass from '@/utils/use-get-font-family-class';

import styles from './edit-project-title.module.css';
import Button from '@ui/buttons/button/button';

type EditProjectTitleProps = {
  defaultName?: string;
  placeholder?: string;
  syncSessionUuid?: string;
  sessionDefaultLanguage?: string;
  absolutePositionError?: boolean;
  renderFocusButtonContent?: () => React.ReactNode;
};

const EditProjectTitle = ({
  defaultName,
  placeholder = 'Give your project a name',
  syncSessionUuid,
  sessionDefaultLanguage,
  renderFocusButtonContent,
}: EditProjectTitleProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { hasPermission: hasPermissions } = useHasPermission({
    allowedRoles: [EPermissions.GroupAdmin, EPermissions.Editor],
  });

  const createProjectErrors = useAppSelector(selectCreateProjectErrors);
  const workingProjectUuid = useAppSelector(getWorkingProjectUuid);
  const workingProjectGlobalTemplateType = useAppSelector(selectProjectGlobalTemplateType);
  const workingProjectName = useAppSelector(selectProjectName);
  const workingProjectChannel = useAppSelector(selectProjectChannel);
  const workingProjectEventId = useAppSelector(getWorkingProjectEventId);
  const workingProjectSettings = useAppSelector(selectProjectSettings);
  const isAutoGeneratedName = useAppSelector(selectProjectIsAutoGeneratedName);
  const userActiveChannel = useAppSelector(getActiveChannel);
  const loadingProject = useAppSelector(getIsLoadingWorkingProject);
  const _defaultLanguage = useAppSelector(selectWorkingProjectDefaultLanguage);

  const dispatch = useAppDispatch();

  const params = useParams();
  const language = params?.language || 'en';
  const defaultLanguage = _defaultLanguage || 'en';

  // On page load if the project name is still auto generated,
  // we set focus on the input to encourage the user to change it.
  const [forcedFocus, setForcedFocus] = useState(false);
  useEffect(() => {
    if (inputRef.current && !forcedFocus && isAutoGeneratedName) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(0, inputRef.current.value?.length || 0, 'forward');
      setForcedFocus(true);
    }
  }, [forcedFocus, isAutoGeneratedName, setForcedFocus]);

  const displayProjectName = useMemo(() => {
    return getDisplayProjectName({
      channel: workingProjectChannel,
      isAutoGeneratedName,
      globalTemplateType: workingProjectGlobalTemplateType,
      projectName: workingProjectName,
    });
  }, [isAutoGeneratedName, workingProjectChannel, workingProjectGlobalTemplateType, workingProjectName]);

  const fontFamilyClass = useGetFontFamilyClass();

  useEffect(() => {
    // clear project name errors on unmount
    return () => {
      dispatch(removeCreateProjectErrorFields({ projectName: null }));
    };
  }, [dispatch]);

  // call focus on next event loop
  const handleFocus = () => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    if (!workingProjectUuid || !userActiveChannel || !workingProjectSettings) return;

    const title = e.target.value.trim();

    if (isAutoGeneratedName) {
      const strippedName = getStrippedProjectAutoNamePart({
        channel: userActiveChannel,
        globalTemplateType: workingProjectGlobalTemplateType,
        projectName: workingProjectName,
        isAutoGeneratedName: true,
      });

      if (`${title} ${strippedName}` === workingProjectName) {
        dispatch(setCreateProjectErrorFields(null));
        return;
      }

      if (title === displayProjectName) {
        dispatch(setCreateProjectErrorFields({ projectName: 'Give your project a name' }));
        return;
      }
    } else {
      if (title === workingProjectName) {
        dispatch(setCreateProjectErrorFields(null));
        return;
      }
    }

    dispatch(setCreateProjectErrorFields(null));

    if (title.length) {
      try {
        const nameExists = await ValidateProjectName({
          projectName: title,
          channel: userActiveChannel,
          eventId: workingProjectEventId ?? 0,
        });
        if (nameExists.exists) {
          dispatch(setCreateProjectErrorFields({ projectName: 'Project name already exists' }));
          return;
        }
      } catch (error) {
        console.error('Error validating project name', error);
        toastTrigger(
          <div className="S1">
            <Text>There was an error validating the project name.</Text>
            <Text>Please refresh and try again.</Text>
          </div>,
          {
            type: 'error',
            autoClose: 10000,
          }
        );
        return;
      }
    } else {
      dispatch(setCreateProjectErrorFields({ projectName: 'Project name required' }));
      return;
    }

    let sessionsToSave: TPartialSessionWithUuid[] | undefined;
    if (syncSessionUuid) {
      const newTranslateKey = updateTranslateKey({
        translateString: title,
        input: title,
        language: language,
        baseLanguage: sessionDefaultLanguage || defaultLanguage,
      });
      sessionsToSave = [
        {
          uuid: syncSessionUuid,
          title: newTranslateKey,
        },
      ];
    }

    dispatch(
      saveWorkingProjectByFields({
        name: title,
        settings: {
          ...workingProjectSettings,
          is_auto_generated_name: false,
        },
        sessionsToSave,
      })
    );
  };

  if (!workingProjectUuid) return null;
  return (
    <form
      className="width-100-percent flex ai-center position-relative"
      onSubmit={(e) => {
        e.preventDefault();
        // Title save occurs on blur. We also want it to happen on enter and for that enter action to trigger a blur.
        // To ensure we do not save the title twice, just trigger a blur on enter and allow that to handle the save
        inputRef.current?.blur();
      }}
    >
      <div className="flex-column flex-1">
        <OptionalComponent display={!!createProjectErrors?.projectName}>
          <div className="position-absolute top--20">
            <Text className="S3" color="var(--semantic-red-500)">
              {createProjectErrors?.projectName || ''}
            </Text>
          </div>
        </OptionalComponent>
        <div className="flex ai-center gap-10">
          <InputWithOverlay
            data-component="EditProjectTitle"
            inputHeight="32px"
            error={!!createProjectErrors?.projectName}
            inputRef={inputRef}
            wrapperClass="flex-1"
            inputProps={{
              defaultValue: defaultName ?? displayProjectName,
              placeholder,
              className: `H4 padding-0 radius-4 font-weight-700 ${styles.projectTitleInput} ${fontFamilyClass}`,
              style: {
                ...(fontFamilyClass && { fontFamily: 'var(--theme-font-head)' }),
                ...(displayProjectName === 'New Project' && { color: 'var(--gray-600)' }),
                width: '100%',
              },
              disabled: !hasPermissions,
              onBlur: handleBlur,
              onFocus: () => {
                if (inputRef.current) {
                  // reset scroll position to the left when focused so the text position matches the overlay
                  inputRef.current.scrollLeft = 0;
                }
              },
            }}
            renderInnerContent={() => {
              if (!loadingProject) return null;
              return (
                <OptionalComponent display delayRender={300}>
                  <div className="flex fd-row jc-center ai-center width-100-percent height-32">
                    <WaitingIndicator />
                  </div>
                </OptionalComponent>
              );
            }}
          />
          <OptionalComponent display={!!renderFocusButtonContent}>
            <Button onClick={handleFocus} className="padding-0">
              {renderFocusButtonContent?.()}
            </Button>
          </OptionalComponent>
        </div>
      </div>
    </form>
  );
};

export default EditProjectTitle;
